/**
 * Founder Control Center v2 · Sección 3 · Growth · SEO Dashboard.
 *
 * Vista read-only del estado SEO público del marketplace.
 *
 * Fuentes (todas existentes · 0 cambios backend · 0 mocks introducidos):
 *   GET /api/seo/sitemap.xml      → XML sitemap (estáticas + /centro/{slug} + /ciudad/*)
 *   GET /api/seo/robots.txt       → robots.txt servido por backend
 *   GET /api/seo/meta/{slug}      → metadata OG + JSON-LD del slug (inspector)
 *   GET /api/seo/related/{slug}   → centros relacionados (internal linking)
 *
 * Decisiones:
 *   - Read-only · sin mutaciones. La activación full de SEO (sitemap inteligente,
 *     keyword tracking, content score) viene con AG-03 en Sprint A. Hasta entonces
 *     este dashboard surface lo que YA está activo (sitemap real, meta real).
 *   - El endpoint /sitemap.xml devuelve XML; lo parseamos client-side con DOMParser
 *     para contar URLs y desglosar por tipo (home · marketplace · centros · ciudades).
 *   - El meta inspector + related inspector usan el slug del usuario sin endpoints nuevos.
 *
 *   Nota: el cliente Axios `api` (lib/api) inyecta /api antes de las rutas. Estos
 *   endpoints son públicos (sin Depends auth) y aceptan cookies/JWT igualmente.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import {
  MagnifyingGlass, Tree, FileText, LinkSimple, ArrowsClockwise, Globe,
  CheckCircle, Warning,
} from "@phosphor-icons/react";

function classifyUrl(loc) {
  try {
    const u = new URL(loc);
    const p = u.pathname;
    if (p === "/" || p === "") return "home";
    if (p === "/marketplace") return "marketplace";
    if (p.startsWith("/centro/")) return "centro";
    if (p.startsWith("/ciudad/")) return "ciudad";
    if (p === "/landing") return "landing";
    if (p === "/pricing") return "pricing";
    return "otro";
  } catch { return "otro"; }
}

export default function SEODashboard() {
  const [sitemap, setSitemap] = useState({ raw: null, urls: [], err: null, loading: true });
  const [robots, setRobots] = useState({ body: null, err: null, loading: true });

  // Inspector slug
  const [slug, setSlug] = useState("");
  const [meta, setMeta] = useState({ data: null, err: null, loading: false });
  const [related, setRelated] = useState({ items: null, err: null, loading: false });

  const loadStatic = async () => {
    setSitemap((s) => ({ ...s, loading: true }));
    setRobots((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/seo/sitemap.xml", { responseType: "text", transformResponse: (v) => v });
      const text = typeof data === "string" ? data : "";
      let urls = [];
      try {
        const doc = new DOMParser().parseFromString(text, "application/xml");
        urls = Array.from(doc.getElementsByTagName("url")).map((u) => ({
          loc: u.getElementsByTagName("loc")[0]?.textContent || "",
          lastmod: u.getElementsByTagName("lastmod")[0]?.textContent || "",
          changefreq: u.getElementsByTagName("changefreq")[0]?.textContent || "",
          priority: u.getElementsByTagName("priority")[0]?.textContent || "",
        }));
      } catch { /* parser errors caught */ }
      setSitemap({ raw: text, urls, err: null, loading: false });
    } catch (e) {
      setSitemap({ raw: null, urls: [], err: e?.message || "no disponible", loading: false });
    }
    try {
      const { data } = await api.get("/seo/robots.txt", { responseType: "text", transformResponse: (v) => v });
      setRobots({ body: typeof data === "string" ? data : "", err: null, loading: false });
    } catch (e) {
      setRobots({ body: null, err: e?.message || "no disponible", loading: false });
    }
  };

  useEffect(() => { loadStatic(); }, []);

  const counts = useMemo(() => {
    const c = { home: 0, marketplace: 0, landing: 0, pricing: 0, centro: 0, ciudad: 0, otro: 0 };
    sitemap.urls.forEach((u) => { c[classifyUrl(u.loc)] += 1; });
    return c;
  }, [sitemap.urls]);

  const inspect = async (e) => {
    e?.preventDefault?.();
    const s = (slug || "").trim().toLowerCase().replace(/^\/+|\/+$/g, "");
    if (!s) return;
    setMeta({ data: null, err: null, loading: true });
    setRelated({ items: null, err: null, loading: true });
    const [m, r] = await Promise.allSettled([
      api.get(`/seo/meta/${encodeURIComponent(s)}`),
      api.get(`/seo/related/${encodeURIComponent(s)}?limit=6`),
    ]);
    setMeta(m.status === "fulfilled"
      ? { data: m.value.data, err: null, loading: false }
      : { data: null, err: m.reason?.response?.status === 404 ? "Slug no encontrado o no público" : (m.reason?.message || "error"), loading: false });
    setRelated(r.status === "fulfilled"
      ? { items: r.value.data?.items || [], err: null, loading: false }
      : { items: null, err: r.reason?.message || "error", loading: false });
  };

  return (
    <div className="space-y-4" data-testid="seo-dashboard-root">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <MagnifyingGlass size={11} weight="fill" /> SEO · estado real
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
            SEO Dashboard
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Lectura read-only de lo YA activo: <code className="text-[var(--ag-text)]">sitemap.xml</code> · <code className="text-[var(--ag-text)]">robots.txt</code> · meta OG + JSON-LD por slug · internal linking. Sin mocks. Sin endpoints nuevos.
          </p>
        </div>
        <button
          type="button"
          onClick={loadStatic}
          disabled={sitemap.loading || robots.loading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 transition-colors disabled:opacity-50"
          data-testid="seo-refresh"
        >
          <ArrowsClockwise size={13} className={(sitemap.loading || robots.loading) ? "animate-spin" : ""} /> Refrescar
        </button>
      </header>

      {/* SITEMAP · counters + raw access */}
      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="seo-sitemap">
        <div className="flex items-center gap-2 mb-3">
          <Tree size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
            Sitemap.xml · indexación
          </h3>
        </div>
        {sitemap.err && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5 mb-3" data-testid="seo-sitemap-error">
            Error cargando sitemap: {sitemap.err}
          </div>
        )}
        {!sitemap.raw && !sitemap.err && sitemap.loading && (
          <div className="text-xs text-[var(--ag-text-dim)]">Cargando sitemap…</div>
        )}
        {sitemap.urls.length > 0 && (
          <>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display text-4xl font-black tabular-nums" data-testid="seo-sitemap-total">{sitemap.urls.length}</span>
              <span className="text-xs text-[var(--ag-text-dim)]">URLs en sitemap</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <Counter label="Home" value={counts.home} testid="seo-c-home" />
              <Counter label="Marketplace" value={counts.marketplace} testid="seo-c-marketplace" />
              <Counter label="Landing" value={counts.landing} testid="seo-c-landing" />
              <Counter label="Pricing" value={counts.pricing} testid="seo-c-pricing" />
              <Counter label="Centros" value={counts.centro} accent="info" testid="seo-c-centros" />
              <Counter label="Ciudades" value={counts.ciudad} accent="info" testid="seo-c-ciudades" />
              <Counter label="Otros" value={counts.otro} testid="seo-c-otros" />
            </div>

            {/* Tabla muestra de centros y ciudades */}
            <details className="mt-4 text-xs">
              <summary className="cursor-pointer text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] inline-flex items-center gap-1">
                <FileText size={11} /> Ver primeros 20 URLs del sitemap
              </summary>
              <div className="overflow-x-auto mt-2 rounded border border-[var(--ag-border)]">
                <table className="w-full text-[11px]" data-testid="seo-sitemap-table">
                  <thead className="bg-[var(--ag-midnight)]/50 text-[10px] uppercase text-[var(--ag-text-muted)]">
                    <tr>
                      <th className="text-left px-2 py-1.5">Tipo</th>
                      <th className="text-left px-2 py-1.5">URL</th>
                      <th className="text-left px-2 py-1.5">Lastmod</th>
                      <th className="text-left px-2 py-1.5">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sitemap.urls.slice(0, 20).map((u, i) => (
                      <tr key={`${u.loc}-${i}`} className="border-t border-[var(--ag-border)]">
                        <td className="px-2 py-1 text-[var(--ag-gold)] tabular-nums">{classifyUrl(u.loc)}</td>
                        <td className="px-2 py-1 text-[var(--ag-text)] font-mono break-all">{u.loc}</td>
                        <td className="px-2 py-1 text-[var(--ag-text-dim)] tabular-nums">{u.lastmod}</td>
                        <td className="px-2 py-1 text-[var(--ag-text-dim)] tabular-nums">{u.priority}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>

            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/seo/sitemap.xml`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-[10px] uppercase tracking-wider text-[var(--ag-gold)] hover:underline"
              data-testid="seo-sitemap-link"
            >
              <LinkSimple size={10} /> Abrir sitemap.xml en nueva pestaña
            </a>
          </>
        )}
      </section>

      {/* ROBOTS.txt */}
      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="seo-robots">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
            Robots.txt · directivas crawler
          </h3>
        </div>
        {robots.err && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5 mb-3" data-testid="seo-robots-error">
            Error cargando robots.txt: {robots.err}
          </div>
        )}
        {robots.body && (
          <pre className="text-[11px] p-3 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)] overflow-x-auto text-[var(--ag-text)]" data-testid="seo-robots-body">
{robots.body}
          </pre>
        )}
      </section>

      {/* META + RELATED inspector */}
      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="seo-inspector">
        <div className="flex items-center gap-2 mb-3">
          <Globe size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
            Inspector · meta OG + JSON-LD + related por slug
          </h3>
        </div>
        <form onSubmit={inspect} className="flex gap-2 flex-wrap" data-testid="seo-inspector-form">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="slug del centro (ej. autoglow-madrid)"
            className="flex-1 min-w-[200px] px-3 py-2 text-sm rounded-sm bg-[var(--ag-midnight)] border border-[var(--ag-border)] text-[var(--ag-text)] placeholder:text-[var(--ag-text-muted)]"
            data-testid="seo-inspector-slug"
          />
          <button
            type="submit"
            disabled={meta.loading || !slug.trim()}
            className="px-4 py-2 text-xs uppercase tracking-wider rounded-sm bg-[var(--ag-gold)] text-black font-bold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="seo-inspector-submit"
          >
            <MagnifyingGlass size={12} weight="bold" />
            Inspeccionar
          </button>
        </form>

        {meta.err && (
          <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1.5 mt-3 inline-flex items-center gap-1.5" data-testid="seo-meta-error">
            <Warning size={12} /> {meta.err}
          </div>
        )}

        {meta.data?.found && (
          <div className="mt-4 grid lg:grid-cols-2 gap-3" data-testid="seo-meta-result">
            <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                <CheckCircle size={11} weight="fill" /> Meta OG
              </div>
              <div className="mt-2 text-xs space-y-1">
                <KV label="Title" value={meta.data?.og?.title} />
                <KV label="Description" value={meta.data?.og?.description} small />
                <KV label="URL" value={meta.data?.og?.url} mono />
                <KV label="Image" value={meta.data?.og?.image} mono small />
                <KV label="Site" value={meta.data?.og?.site_name} />
              </div>
            </div>
            <div className="rounded border border-[var(--ag-border)] p-3">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">
                Schema.org LocalBusiness
              </div>
              <pre className="mt-2 text-[10px] p-2 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)] overflow-auto max-h-48 text-[var(--ag-text-dim)]" data-testid="seo-meta-schema">
{JSON.stringify(meta.data?.schema_org, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {related.items != null && (
          <div className="mt-4" data-testid="seo-related-result">
            <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold mb-2">
              Internal linking · centros relacionados ({related.items.length})
            </div>
            {related.items.length === 0 ? (
              <div className="text-xs text-[var(--ag-text-dim)] italic">Sin centros relacionados todavía.</div>
            ) : (
              <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {related.items.map((r) => (
                  <li key={r.slug} className="rounded border border-[var(--ag-border)] p-2 text-xs" data-testid={`seo-related-${r.slug}`}>
                    <div className="font-bold text-[var(--ag-text)] truncate">{r.name}</div>
                    <div className="text-[10px] text-[var(--ag-text-dim)] truncate">{r.city || r.locality} · /centro/{r.slug}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <div className="rounded border border-[var(--ag-gold)]/25 bg-[var(--ag-gold)]/5 p-3 text-[11px] text-[var(--ag-text-dim)]">
        <strong className="text-[var(--ag-gold)] uppercase tracking-wider text-[10px]">Activación full · Sprint A · AG-03 SEO Agent</strong>
        <div className="mt-1">
          Lo que verás aquí cuando AG-03 esté activo: keyword tracking automatizado · content score por slug · sugerencias de meta optimizadas · alertas de páginas con baja conversión orgánica · auto-actualización del sitemap por cambios reales en `ecosystem_catalog`. Hoy: indexación y meta JSON-LD ya funcionan en producción contra el marketplace público.
        </div>
      </div>
    </div>
  );
}

function Counter({ label, value, accent, testid }) {
  const cls = accent === "info"
    ? "border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]"
    : "border-[var(--ag-border)] bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)]";
  return (
    <div className={`rounded border px-2.5 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function KV({ label, value, mono = false, small = false }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)]">{label}</span>
      <span className={`break-all ${mono ? "font-mono" : ""} ${small ? "text-[11px]" : "text-xs"} text-[var(--ag-text)]`}>{value}</span>
    </div>
  );
}
